import React, { useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useRouteMatch } from "react-router-dom";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { Divider, Stack } from "@mui/material";
import { WorkspaceAllFilesIcon, WorkspaceIcon, WorkspaceRecentlyAddedIcon } from "assets/material-custom-icons";
import { ArrowTrigger } from "components/old-data-box-components";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";

import { Accordion } from "./Accordion";
import { AddNewMenu } from "./AddNewMenu";
import { DealsFoldersList } from "./DealsFoldersList";
import { FoldersList } from "./FoldersList";
import { NavItem } from "./NavItem";

const COLLAPSED_WIDTH = 300;
const EXPANDED_WIDTH = 600;

/** Routes that show deal/folder content; when pathname matches any of these, the Deal folders accordion opens. */
const WORKSPACE_DEAL_ROUTE_PATTERNS = [Routes.workspaceDeal, Routes.workspaceDealFolder];

/** Routes that show global (unassigned) folder content. */
const WORKSPACE_GLOBAL_ROUTE_PATTERNS = [
  Routes.workspaceFolder,
  Routes.workspaceFolderDocument,
  Routes.workspaceFolderForm
];

type AccordionType = "global" | "deal" | "cloudMirror";

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [accordionType, setAccordionType] = useState<AccordionType | null>(null);

  const allFilesMatch = useRouteMatch({ path: Routes.workspaceAllFiles, exact: true });
  const recentlyAddedMatch = useRouteMatch({ path: Routes.workspaceRecentlyAdded, exact: true });
  const isWorkspaceDealRoute = WORKSPACE_DEAL_ROUTE_PATTERNS.some((path) => matchPath(pathname, { path }));
  const isGlobalFolderRoute = WORKSPACE_GLOBAL_ROUTE_PATTERNS.some((path) => matchPath(pathname, { path }));
  const isGlobalWorkspaceRoot = useRouteMatch({ path: Routes.workspaceFolders, exact: true });
  const isGlobalWorkspaceDealRoute = useRouteMatch({ path: Routes.workspaceDeals, exact: true });

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isWorkspaceDealRoute && accordionType !== "deal") {
      setAccordionType("deal");
    } else if (isGlobalFolderRoute && accordionType !== "global") {
      setAccordionType("global");
    }
  }, [pathname]);

  const [availableHeight, setAvailableHeight] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const first = el.firstElementChild;
    const last = el.lastElementChild;

    const contentHeight =
      (last as HTMLElement).getBoundingClientRect().bottom - (first as HTMLElement).getBoundingClientRect().top;
    const update = () => {
      setAvailableHeight(el.offsetHeight - contentHeight);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Stack
      ref={contentRef}
      sx={{
        minHeight: 0,
        minWidth: `${COLLAPSED_WIDTH}px`,
        width: isExpanded ? `${EXPANDED_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
        height: "100%",
        maxHeight: "100%",
        backgroundColor: "#f6f8fc",
        overflow: "hidden",
        transition: "width 0.3s ease-in-out, box-shadow 0.3s ease-in-out, left 0.3s ease-in-out",
        ...(isExpanded && {
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: 10,
          boxShadow: "4px 0 16px rgba(0,0,0,0.15)"
        })
      }}
    >
      <Stack
        sx={{
          flexShrink: 0,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "5px"
        }}
      >
        <Stack direction="row" alignItems="center" gap="5px">
          <AddNewMenu />
        </Stack>
        <ArrowTrigger
          onClick={() => setIsExpanded((v) => !v)}
          isOpen={isExpanded}
          closeState="right"
          openState="left"
          sx={{ ml: 0.5 }}
        />
      </Stack>
      <NavItem
        icon={<WorkspaceAllFilesIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
        label="All Items"
        isActive={!!allFilesMatch}
        onClick={() => navigate({ route: "workspaceAllFiles" })}
      />
      <NavItem
        icon={<WorkspaceRecentlyAddedIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
        label="Recently Added"
        isActive={!!recentlyAddedMatch}
        onClick={() => navigate({ route: "workspaceRecentlyAdded" })}
        hint={{
          title: "Shows files added within the last 3 months",
          icon: <InfoOutlinedIcon sx={{ fontSize: 15, color: "#0A3142", opacity: 0.7 }} />
        }}
      />
      <Divider
        sx={{
          margin: "5px 10px 5px 0",
          backgroundColor: "none",
          borderBlockColor: "#DBE8F5 !important",
          height: "1px",
          flexShrink: 0
        }}
      />
      <Accordion
        title="Unassigned"
        isOpen={accordionType === "global"}
        onArrowClick={() => setAccordionType((prev) => (prev === "global" ? null : "global"))}
        onTitleClick={() => navigate({ route: "workspaceFolders" })}
        isActive={!!isGlobalWorkspaceRoot}
        icon={<WorkspaceIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
      >
        <FoldersList maxHeight={availableHeight} />
      </Accordion>
      {/* <Divider
        sx={{
          margin: "5px 10px 5px 0",
          backgroundColor: "none",
          borderBlockColor: "#DBE8F5 !important",
          height: "1px",
          flexShrink: 0
        }}
      />
      <NavItem
        icon={<WorkspaceAllFilesIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
        label="All Files"
        isActive={!!allFilesMatch}
        onClick={() => navigate({ route: "workspaceAllFiles" })}
      />
      <NavItem
        icon={<WorkspaceRecentlyAddedIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
        label="Recently Added"
        isActive={!!recentlyAddedMatch}
        onClick={() => navigate({ route: "workspaceRecentlyAdded" })}
        hint={{
          title: "Shows files added within the last 3 months",
          icon: <InfoOutlinedIcon sx={{ fontSize: 15, color: "#0A3142", opacity: 0.7 }} />
        }}
      /> */}
      <Accordion
        title="Deal folders"
        icon={<LocationCityIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
        isOpen={accordionType === "deal"}
        onArrowClick={() => setAccordionType((prev) => (prev === "deal" ? null : "deal"))}
        onTitleClick={() => navigate({ route: "workspaceDeals" })}
        isActive={!!isGlobalWorkspaceDealRoute}
      >
        <DealsFoldersList availableHeight={availableHeight} />
      </Accordion>
      {/* <Accordion
        title="Cloud Mirror"
        icon={<Cloud sx={{ fill: "#0A3142", fontSize: 20 }} />}
        isOpen={accordionType === "cloudMirror"}
        onArrowClick={() => setAccordionType((prev) => (prev === "cloudMirror" ? null : "cloudMirror"))}
        onTitleClick={() => setAccordionType((prev) => (prev === "cloudMirror" ? null : "cloudMirror"))}
      />
      <Stack sx={{ flexShrink: 0, minHeight: 0 }}>
        <CloudMirror availableHeight={availableHeightForFolders} />
      </Stack> */}
    </Stack>
  );
};
